import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiHelper } from '../apiHelper/ApiHelper';
import { readJson } from '../apiHelper/readJson';
import { resolveFileUrl } from '../Constants';
import './styles/QuizRunner.scss';

type Option = { id: string; text: string };
type Question = { id: string; text: string; imageUrl?: string | null; type: number; topic: string; difficulty: number; options: Option[] };
type Answer = { questionId: string; selectedOptionIds: string[] };
type CheckResult = { correct: boolean; explanation: string; correctOptionIds: string[]; selectedOptionIds: string[] };

function shuffle<T>(items: T[]): T[] {
    const out = [...items];
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
}

function format(sec: number) {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return m + ":" + s.toString().padStart(2, "0");
}

export default function QuizRunner({ questions, timeLimitSeconds, initialIdx = 0, initialAnswers = {}, subject = null, onProgress, onFinish, onQuit }: {
    questions: Question[];
    timeLimitSeconds?: number | null;
    initialIdx?: number;
    initialAnswers?: { [qid: string]: string[] };
    subject?: number | null;
    onProgress?: (idx: number, answers: { [qid: string]: string[] }) => void;
    onFinish: (answers: Answer[]) => void;
    onQuit: () => void;
}) {
    const navigate = useNavigate();
    const [idx, setIdx] = useState(initialIdx);
    const [answers, setAnswers] = useState<{ [qid: string]: string[] }>(initialAnswers);
    const [feedback, setFeedback] = useState<"end" | "each">("end");
    const [revealed, setRevealed] = useState<CheckResult | null>(null);
    const [remaining, setRemaining] = useState<number | null>(timeLimitSeconds ?? null);

    const api = new ApiHelper();
    const timed = (timeLimitSeconds ?? 0) > 0;
    const mixed = new Set(questions.map(qq => qq.type)).size > 1;
    const shuffled = useMemo(() => questions.map(qq => ({ ...qq, options: shuffle(qq.options) })), [questions]);
    const q = shuffled[idx];
    const selected = answers[q.id] ?? [];
    const multi = mixed || q.type === 1;

    function finish() {
        onFinish(questions.map(qq => ({ questionId: qq.id, selectedOptionIds: answers[qq.id] ?? [] })));
    }

    useEffect(() => {
        if (onProgress) onProgress(idx, answers);
    }, [idx, answers]);

    useEffect(() => {
        if (remaining === null) return;
        if (remaining <= 0) { finish(); return; }
        const t = setTimeout(() => setRemaining(r => (r ?? 0) - 1), 1000);
        return () => clearTimeout(t);
    }, [remaining]);

    function toggle(optId: string) {
        if (revealed) return;
        if (!multi) {
            setAnswers({ ...answers, [q.id]: [optId] });
        } else {
            const cur = answers[q.id] ?? [];
            setAnswers({ ...answers, [q.id]: cur.includes(optId) ? cur.filter(x => x !== optId) : [...cur, optId] });
        }
    }

    async function check() {
        try {
            const res = await api.practiceApi.checkCreate({ questionId: q.id, selectedOptionIds: selected });
            setRevealed(await readJson(res));
        } catch (x: any) { api.processErrors(x); }
    }

    function next() {
        setRevealed(null);
        if (idx + 1 < questions.length) setIdx(idx + 1);
        else finish();
    }

    function prev() {
        if (idx === 0) return;
        setRevealed(null);
        setIdx(idx - 1);
    }

    function askTutor() {
        if (!revealed) return;
        const correctText = q.options.filter(o => revealed.correctOptionIds.includes(o.id)).map(o => o.text).join("; ");
        const context = `Otázka: "${q.text}". Správná odpověď: ${correctText}. Vysvětlení: ${revealed.explanation}`;
        navigate("/kurz/tutor", { state: { tutorQuestion: `Nerozumím této otázce, můžeš mi ji vysvětlit? „${q.text}"`, tutorContext: context, tutorSubject: subject ?? undefined } });
    }

    const last = idx + 1 === questions.length;
    const optionClass = (optId: string) => {
        if (!revealed) return selected.includes(optId) ? "opt selected" : "opt";
        const isCorrect = revealed.correctOptionIds.includes(optId);
        const isSel = selected.includes(optId);
        if (isCorrect) return "opt correct";
        if (isSel) return "opt wrong";
        return "opt";
    };

    return (
        <div className="quizRunner">
            <div className="qHead">
                <div className="qTop">
                    <span className="qProgress">Otázka {idx + 1} / {questions.length}</span>
                    {timed && remaining !== null && <span className={"qTimer" + (remaining < 60 ? " low" : "")}>{format(remaining)}</span>}
                    <span className="qQuit" onClick={onQuit}>Ukončit</span>
                </div>
                <div className="qBar"><div className="qBarFill" style={{ width: ((idx) / questions.length * 100) + "%" }} /></div>
                {!timed &&
                    <div className="qFeedback">
                        <span>Zpětná vazba:</span>
                        <div className={"fb" + (feedback === "each" ? " on" : "")} onClick={() => setFeedback("each")}>Po každé otázce</div>
                        <div className={"fb" + (feedback === "end" ? " on" : "")} onClick={() => setFeedback("end")}>Na konci</div>
                    </div>
                }
            </div>

            <div className="qCard">
                <span className="qTopic">{q.topic}</span>
                <h2 className="qText">{q.text}</h2>
                {q.imageUrl && <img className="qImage" src={resolveFileUrl(q.imageUrl)} alt="" />}
                <span className="qHint">{mixed ? "Vyber odpovědi, které považuješ za správné" : q.type === 1 ? "Vyber všechny správné odpovědi" : "Vyber jednu odpověď"}</span>

                <div className="qOptions">
                    {q.options.map(o => (
                        <div key={o.id} className={optionClass(o.id)} onClick={() => toggle(o.id)}>
                            <span className="mark">{multi ? "▢" : "◯"}</span>
                            <span>{o.text}</span>
                        </div>
                    ))}
                </div>

                {revealed &&
                    <div className={"qExpl " + (revealed.correct ? "ok" : "no")}>
                        <b>{revealed.correct ? "Správně!" : "Špatně."}</b> {revealed.explanation}
                        {!revealed.correct && <div className="askTutor" onClick={askTutor}>Zeptat se AI tutora na tuto otázku →</div>}
                    </div>
                }
            </div>

            <div className="qActions">
                <div className={"qBtn prev" + (idx === 0 ? " disabled" : "")} onClick={prev}>← Předchozí</div>
                {feedback === "each" && !revealed ?
                    <div className={"qBtn primary" + (selected.length === 0 ? " disabled" : "")} onClick={() => selected.length > 0 && check()}>Zkontrolovat</div>
                    :
                    <div className="qBtn primary" onClick={next}>{last ? "Dokončit" : "Další otázka"}</div>
                }
            </div>
        </div>
    );
}
